import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, HardDrive, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface SetupStatus {
  needsSetup?: boolean;
  hasCompanyName?: boolean;
  canRestore?: boolean;
}

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const MAX_BACKUP_BYTES = 512 * 1024 * 1024;

const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const [companyName, setCompanyName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const backupInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const status = await apiService.get<SetupStatus>('/system/public/setup-status');
        if (status?.hasCompanyName === true) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
      } finally {
        setChecking(false);
      }
    };

    load();
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Die Datei ist zu groß. Maximale Größe: 5 MB');
      event.target.value = '';
      return;
    }

    setLogoFile(file);
    setLogoPreviewUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return URL.createObjectURL(file);
    });
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return null;
    });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = companyName.trim();
    if (!trimmed) {
      toast.error('Bitte geben Sie einen Firmennamen ein');
      return;
    }

    setSaving(true);
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        await apiService.post('/system/logo/upload', formData, {
          timeout: 60000
        });
      }

      await apiService.put('/system/company_name', { value: trimmed });
      toast.success('Einrichtung abgeschlossen');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Error saving first-run setup:', error);
      const message = error.response?.data?.error || error.message || 'Unbekannter Fehler';
      toast.error(`Einrichtung fehlgeschlagen: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBackupSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const name = file.name.toLowerCase();
    if (!name.endsWith('.tar.gz') && !name.endsWith('.tgz') && !name.endsWith('.zip')) {
      toast.error('Bitte wählen Sie ein Backup-Archiv (.tar.gz oder .zip)');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_BACKUP_BYTES) {
      toast.error('Die Backup-Datei ist zu groß (maximal 512 MB)');
      event.target.value = '';
      return;
    }

    setBackupFile(file);
    setRestoreError(null);
  };

  const handleRestore = async () => {
    if (!backupFile) {
      toast.error('Bitte wählen Sie eine Backup-Datei aus');
      return;
    }
    if (!restoreConfirmed) {
      toast.error('Bitte bestätigen Sie, dass die aktuellen Daten überschrieben werden');
      return;
    }

    setRestoring(true);
    setRestoreError(null);
    try {
      const formData = new FormData();
      formData.append('backup', backupFile);
      await apiService.post('/system/setup/restore', formData, {
        timeout: 600000
      });
      toast.success(
        'Backup wurde eingespielt. Bitte erneut anmelden. Benutzer und Rollen stammen jetzt aus dem Backup.'
      );
      logout();
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Error restoring backup during setup:', error);
      const message =
        error.response?.data?.error ||
        error.message ||
        'Wiederherstellung fehlgeschlagen';
      setRestoreError(message);
      toast.error(`Wiederherstellung fehlgeschlagen: ${message}`);
    } finally {
      setRestoring(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-xl space-y-8">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">Initialer Setup</h1>
          <p className="text-muted-foreground">
            Richten Sie diese Instanz neu ein oder spielen Sie ein vorhandenes Backup ein.
            Die Einrichtung wird erst abgeschlossen, wenn Sie ausdrücklich speichern oder wiederherstellen.
          </p>
        </div>

        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleLogoSelect}
          onClick={(event) => event.stopPropagation()}
        />

        <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border bg-card p-6 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold">Neue Installation</h2>
            <p className="text-sm text-muted-foreground">
              Firmenname ist erforderlich. Ein Logo ist optional und wird erst beim Abschluss hochgeladen.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name">Firmenname</Label>
            <Input
              id="company_name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z. B. Westfalen AG"
              autoFocus
              required
            />
            <p className="text-xs text-muted-foreground">
              Wird auf der Anmeldeseite und in der Anwendung angezeigt.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Firmenlogo (optional)</p>
            <div className="rounded-lg border border-dashed p-4 space-y-3">
              {logoPreviewUrl && logoFile ? (
                <div className="flex items-center gap-4">
                  <img
                    src={logoPreviewUrl}
                    alt="Logo-Vorschau"
                    className="h-16 w-auto max-w-48 object-contain border rounded bg-background"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{logoFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Wird erst beim Abschließen der Einrichtung gespeichert.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={clearLogo}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Noch keine Datei ausgewählt. JPG, PNG, GIF oder SVG, maximal 5 MB.
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={saving || restoring}
              >
                <Upload className="h-4 w-4 mr-2" />
                Logo auswählen
              </Button>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={saving || restoring}>
            {saving ? 'Speichern...' : 'Einrichtung abschließen'}
          </Button>
        </form>

        <div className="space-y-6 rounded-lg border bg-card p-6 shadow-sm">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Backup einspielen
            </h2>
            <p className="text-sm text-muted-foreground">
              Laden Sie ein Archiv (.tar.gz oder .zip) im gleichen Format wie die Datensicherung hoch:
              <span className="font-mono"> database.sql</span>, <span className="font-mono">uploads/</span> und{' '}
              <span className="font-mono">meta.json</span>. Maximal 512 MB.
            </p>
            <p className="text-sm text-muted-foreground">
              Durch die erste Entra-Anmeldung existiert bereits ein Superadmin-Konto. Das Backup ersetzt
              alle aktuellen Daten einschließlich dieses Benutzers und aller Rollen. Danach ist die Instanz
              nicht mehr im Erstsetup — Sie müssen sich erneut anmelden.
            </p>
          </div>

          <input
            ref={backupInputRef}
            type="file"
            accept=".tar.gz,.tgz,.zip,.gz,application/gzip,application/zip"
            className="hidden"
            onChange={handleBackupSelect}
            onClick={(event) => event.stopPropagation()}
          />

          <div className="rounded-lg border border-dashed p-4 space-y-3">
            <p className="text-sm">
              {backupFile ? (
                <span className="font-medium">{backupFile.name}</span>
              ) : (
                <span className="text-muted-foreground">Noch keine Backup-Datei ausgewählt.</span>
              )}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => backupInputRef.current?.click()}
              disabled={saving || restoring}
            >
              <Upload className="h-4 w-4 mr-2" />
              Backup-Datei auswählen
            </Button>
          </div>

          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              checked={restoreConfirmed}
              onCheckedChange={(value) => setRestoreConfirmed(value === true)}
              disabled={saving || restoring}
            />
            <span>
              Ich verstehe, dass die aktuellen Daten — einschließlich des soeben angelegten Superadmin-Kontos —
              durch das Backup überschrieben werden.
            </span>
          </label>

          {restoreError && (
            <p className="text-sm text-destructive">{restoreError}</p>
          )}

          <Button
            type="button"
            className="w-full"
            onClick={handleRestore}
            disabled={saving || restoring || !backupFile || !restoreConfirmed}
          >
            {restoring ? 'Wird wiederhergestellt...' : 'Backup einspielen'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetupPage;
